import { fmtDate, fmtMonthYear, fmtTime } from '../utils/datetime';
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Calendar, ChevronLeft, ChevronRight, Clock, Filter,
    MapPin, RefreshCw, User, Users, X,
} from "lucide-react";
import { GoogleMap, Marker, useJsApiLoader, InfoWindow } from "@react-google-maps/api";
import PageLayout from "../components/PageLayout";
import config from "../config";
import { useAuth } from "../context/AuthContext";
import { getAllEmployees } from "../api/employee";
import { getAllEmployeeAttendance, getAttendanceReport } from "../api/report";

// ── Types ─────────────────────────────────────────────────────────────────────

interface SubEmp {
    employee_id: number;
    employee_name: string | null;
    employee_code: string | null;
    designation: string | null;
    designation_rank: number | null;
    headquarter: string | null;
    zone: string | null;
    status: "present" | "absent" | "leave";
    location_type: string | null;
    ta_type: number | null;
    route: string | null;
    signin: string | null;
    signout: string | null;
    start_latitude: number | null;
    start_longitude: number | null;
    end_latitude: number | null;
    end_longitude: number | null;
}

interface DailyAttData {
    date: string;
    total: number;
    present_count: number;
    absent_count: number;
    leave_count: number;
    subordinates: SubEmp[];
}

interface MonthlyDay {
    date: string;
    day: string;
    status: "present" | "absent" | "leave" | "sunday" | "holiday";
    location_type: string | null;
    ta_type: number | null;
    route: string | null;
    holiday_name: string | null;
    leave_type: string | null;
    signin: string | null;
    signout: string | null;
    start_latitude: number | null;
    start_longitude: number | null;
    end_latitude: number | null;
    end_longitude: number | null;
}

interface MonthlyAttData {
    month: string;
    employee_id: number;
    employee_name: string | null;
    employee_code: string | null;
    designation: string | null;
    total_days: number;
    working_days: number;
    present_count: number;
    leave_count: number;
    holiday_count: number;
    absent_count: number;
    sunday_count: number;
    daily: MonthlyDay[];
}

interface EmployeeOption { id: number; name: string; code: string; designation: string }

// ── Constants ─────────────────────────────────────────────────────────────────

const MAPS_KEY = config.googleMapsApiKey;
const MAP_CONTAINER = { width: "100%", height: "100%" };
const INDIA_CENTER  = { lat: 22.5, lng: 80.0 };

const STATUS_STYLE: Record<string, string> = {
    present: "bg-green-100 text-green-800",
    absent:  "bg-red-100 text-red-800",
    leave:   "bg-yellow-100 text-yellow-800",
    holiday: "bg-purple-100 text-purple-800",
    sunday:  "bg-gray-100 text-gray-600",
};

const STATUS_CELL: Record<string, string> = {
    present: "bg-green-500 text-white",
    absent:  "bg-red-400 text-white",
    leave:   "bg-yellow-400 text-white",
    holiday: "bg-purple-400 text-white",
    sunday:  "bg-gray-200 text-gray-500",
};

// ta_type: 1 = one-way, 2 = two-way (see AttendanceService::resolveOutStationTaType)
const TaTypeBadge = ({ taType }: { taType: number | null }) => {
    if (taType !== 1 && taType !== 2) return null;
    const isOneWay = taType === 1;
    return (
        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${isOneWay ? "bg-purple-100 text-purple-800" : "bg-teal-100 text-teal-800"}`}>
            {isOneWay ? "One-way" : "Two-way"}
        </span>
    );
};

const LOCATION_STYLE: Record<string, string> = {
    hq: "bg-blue-50 text-blue-700",
    ex: "bg-indigo-50 text-indigo-700",
    out: "bg-orange-50 text-orange-700",
    transit: "bg-cyan-50 text-cyan-700",
    other: "bg-gray-100 text-gray-600",
};

const LocationBadge = ({ locationType }: { locationType: string | null }) => {
    if (!locationType) return <span className="text-gray-300 text-xs">—</span>;
    return (
        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${LOCATION_STYLE[locationType] ?? "bg-gray-100 text-gray-600"}`}>
            {locLabel[locationType] ?? locationType}
        </span>
    );
};

// Location badge + route + TA direction, stacked in one compact cell so the
// table doesn't need a separate column per attribute.
const WorkDetailsCell = ({ locationType, route, taType }: { locationType: string | null; route: string | null; taType: number | null }) => (
    <div className="space-y-1">
        <div className="flex items-center gap-1.5 flex-wrap">
            <LocationBadge locationType={locationType} />
            <TaTypeBadge taType={taType} />
        </div>
        {route && (
            <div className="text-xs text-gray-500 truncate max-w-[220px]" title={route}>
                {route}
            </div>
        )}
    </div>
);

const TimeCell = ({ signin, signout }: { signin: string | null; signout: string | null }) => (
    <div className="text-xs font-mono leading-relaxed">
        <div className="text-gray-700">In&nbsp; {fmtTime(signin)}</div>
        <div className="text-gray-400">Out {fmtTime(signout)}</div>
    </div>
);

function toDateStr(d: Date) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function toMonthStr(d: Date) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}


const locLabel: Record<string, string> = { hq: "HQ", ex: "Ex-Station", out: "Out-Station", transit: "Transit", other: "Other" };

// ── Shared helpers ────────────────────────────────────────────────────────────

const Skeleton = ({ rows = 5 }: { rows?: number }) => (
    <div className="animate-pulse space-y-2">
        {Array.from({ length: rows }).map((_, i) => (
            <div key={i} className="h-10 bg-gray-100 rounded-lg" />
        ))}
    </div>
);

const StatCard = ({ label, value, color }: { label: string; value: number; color: string }) => (
    <div className={`rounded-xl p-4 ${color}`}>
        <p className="text-xs font-semibold uppercase tracking-wide opacity-75 mb-1">{label}</p>
        <p className="text-3xl font-extrabold">{value}</p>
    </div>
);

// ── Page ─────────────────────────────────────────────────────────────────────

interface Props { onLogout: () => void; onNavigate: (page: string) => void }

const AttendancePage: React.FC<Props> = ({ onLogout, onNavigate }) => {
    const { user } = useAuth();
    const [tab, setTab]       = useState<"daily" | "monthly">("daily");

    // ── Daily state ───────────────────────────────────────────────────────────
    const today = toDateStr(new Date());
    const [dailyDate, setDailyDate]       = useState(today);
    const [dailyData, setDailyData]       = useState<DailyAttData | null>(null);
    const [dailyLoading, setDailyLoading] = useState(false);
    const [dailyError, setDailyError]     = useState<string | null>(null);
    const [statusFilter, setStatusFilter] = useState<"all" | "present" | "absent" | "leave">("all");
    const [selectedEmpId, setSelectedEmpId] = useState<number | null>(null);
    const [infoEmp, setInfoEmp]             = useState<SubEmp | null>(null);

    // ── Monthly state ─────────────────────────────────────────────────────────
    const [employees, setEmployees]         = useState<EmployeeOption[]>([]);
    const [monthlyEmpId, setMonthlyEmpId]   = useState<number | null>(null);
    const [monthlyMonth, setMonthlyMonth]   = useState(toMonthStr(new Date()));
    const [monthlyData, setMonthlyData]     = useState<MonthlyAttData | null>(null);
    const [monthlyLoading, setMonthlyLoading] = useState(false);
    const [monthlyError, setMonthlyError]   = useState<string | null>(null);
    const [mapDay, setMapDay]               = useState<MonthlyDay | null>(null);

    const mapRef = useRef<google.maps.Map | null>(null);

    const { isLoaded } = useJsApiLoader({ id: "gmap", googleMapsApiKey: MAPS_KEY });

    // ── Fetch employees (monthly only) ───────────────────────────────────────
    useEffect(() => {
        if (!user?.tenant_id) return;
        getAllEmployees(user.tenant_id).then((empList: any[]) => {
            const list = (Array.isArray(empList) ? empList : []).map((e: any) => ({
                id: e.id,
                name: e.user?.name ?? e.name ?? "",
                code: e.employee_code ?? e.code ?? "",
                designation: e.designation?.name ?? "",
            }));
            setEmployees(list);
            if (list.length > 0) setMonthlyEmpId(list[0].id);
        }).catch(() => {});
    }, [user?.tenant_id]);

    // ── Fetch daily (all employees, no manager filter) ───────────────────────
    const fetchDaily = useCallback(async () => {
        if (!user?.tenant_id) return;
        setDailyLoading(true); setDailyError(null);
        try {
            const res = await getAllEmployeeAttendance(user.tenant_id, dailyDate);
            setDailyData(res.data?.data ?? null);
        } catch (e: any) {
            setDailyError(e?.response?.data?.message ?? "Failed to load attendance");
        } finally { setDailyLoading(false); }
    }, [user?.tenant_id, dailyDate]);

    useEffect(() => { if (tab === "daily") fetchDaily(); }, [fetchDaily, tab]);

    // ── Fit daily map to markers ──────────────────────────────────────────────
    useEffect(() => {
        if (!isLoaded || !mapRef.current || !dailyData) return;
        const coords = (dailyData.subordinates ?? [])
            .filter(e => e.start_latitude && e.start_longitude)
            .map(e => ({ lat: e.start_latitude!, lng: e.start_longitude! }));
        if (coords.length === 0) return;
        const bounds = new window.google.maps.LatLngBounds();
        coords.forEach(c => bounds.extend(c));
        mapRef.current.fitBounds(bounds, 60);
    }, [isLoaded, dailyData]);

    // ── Fetch monthly ─────────────────────────────────────────────────────────
    const fetchMonthly = useCallback(async () => {
        if (!user?.tenant_id || !monthlyEmpId) return;
        setMonthlyLoading(true); setMonthlyError(null);
        try {
            const res = await getAttendanceReport(user.tenant_id, monthlyEmpId, monthlyMonth);
            setMonthlyData(res.data?.data ?? null);
        } catch (e: any) {
            setMonthlyError(e?.response?.data?.message ?? "Failed to load attendance");
        } finally { setMonthlyLoading(false); }
    }, [user?.tenant_id, monthlyEmpId, monthlyMonth]);

    useEffect(() => { if (tab === "monthly") fetchMonthly(); }, [fetchMonthly, tab]);

    // ── Daily derived ─────────────────────────────────────────────────────────
    const filteredSubs = useMemo(() => {
        const subs = dailyData?.subordinates ?? [];
        return statusFilter === "all" ? subs : subs.filter(e => e.status === statusFilter);
    }, [dailyData, statusFilter]);

    // ── Monthly calendar grid ─────────────────────────────────────────────────
    const calendarGrid = useMemo(() => {
        if (!monthlyData) return [];
        const firstDay = new Date(monthlyData.month + "-01T00:00:00").getDay();
        const blanks   = Array.from({ length: firstDay }, () => null);
        return [...blanks, ...monthlyData.daily];
    }, [monthlyData]);

    // ── Marker icons ──────────────────────────────────────────────────────────
    const checkinIcon  = isLoaded ? { url: "https://maps.google.com/mapfiles/ms/icons/green-dot.png" } : undefined;
    const checkoutIcon = isLoaded ? { url: "https://maps.google.com/mapfiles/ms/icons/red-dot.png"   } : undefined;

    return (
        <PageLayout onLogout={onLogout} currentPage="attendance" onNavigate={onNavigate}>
                {/* ── Header ── */}
                <header className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                    <div>
                        <h1 className="text-xl font-bold text-gray-900">Attendance</h1>
                        <p className="text-sm text-gray-500 mt-0.5">Track field employee attendance with check-in / check-out locations</p>
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setTab("daily")}
                            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === "daily" ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                        >Daily Overview</button>
                        <button
                            onClick={() => setTab("monthly")}
                            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === "monthly" ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                        >Monthly Detail</button>
                    </div>
                </header>

                <div className="p-6">

                    {/* ════════════════════════════════════════════ DAILY TAB */}
                    {tab === "daily" && (
                        <div className="space-y-5">
                            {/* Controls */}
                            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex flex-wrap gap-3 items-center">
                                <div className="flex items-center gap-2">
                                    <Calendar className="w-4 h-4 text-gray-400" />
                                    <input
                                        type="date"
                                        value={dailyDate}
                                        max={today}
                                        onChange={e => setDailyDate(e.target.value)}
                                        className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                <div className="flex items-center gap-2">
                                    <Filter className="w-4 h-4 text-gray-400" />
                                    <select
                                        value={statusFilter}
                                        onChange={e => setStatusFilter(e.target.value as any)}
                                        className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="all">All Status</option>
                                        <option value="present">Present</option>
                                        <option value="absent">Absent</option>
                                        <option value="leave">On Leave</option>
                                    </select>
                                </div>
                                <button
                                    onClick={fetchDaily}
                                    className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                                >
                                    <RefreshCw className="w-3.5 h-3.5" /> Refresh
                                </button>
                            </div>

                            {/* Stats row */}
                            {dailyData && (
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <StatCard label="Total"   value={dailyData.total}         color="bg-blue-600 text-white" />
                                    <StatCard label="Present" value={dailyData.present_count} color="bg-green-50 text-green-800" />
                                    <StatCard label="Absent"  value={dailyData.absent_count}  color="bg-red-50 text-red-800" />
                                    <StatCard label="On Leave" value={dailyData.leave_count}  color="bg-yellow-50 text-yellow-800" />
                                </div>
                            )}

                            {/* Table + Map split */}
                            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                                {/* Table */}
                                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                                    <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                                        <h2 className="font-semibold text-gray-800 flex items-center gap-2">
                                            <Users className="w-4 h-4 text-blue-500" /> Employees
                                            {filteredSubs.length > 0 && (
                                                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-bold">{filteredSubs.length}</span>
                                            )}
                                        </h2>
                                        {selectedEmpId && (
                                            <button onClick={() => { setSelectedEmpId(null); setInfoEmp(null); }} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800">
                                                <X className="w-3 h-3" /> Clear selection
                                            </button>
                                        )}
                                    </div>
                                    <div className="overflow-auto max-h-[480px]">
                                        {dailyLoading ? (
                                            <div className="p-5"><Skeleton /></div>
                                        ) : dailyError ? (
                                            <div className="p-8 text-center text-red-500 text-sm">{dailyError}</div>
                                        ) : filteredSubs.length === 0 ? (
                                            <div className="p-8 text-center text-gray-400 text-sm">No records found</div>
                                        ) : (
                                            <table className="w-full text-sm">
                                                <thead className="bg-gray-50 sticky top-0">
                                                    <tr>
                                                        {["Employee","Status","Time","Work Details"].map(h => (
                                                            <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {filteredSubs.map(emp => (
                                                        <tr
                                                            key={emp.employee_id}
                                                            onClick={() => { setSelectedEmpId(emp.employee_id); setInfoEmp(emp); }}
                                                            className={`border-t border-gray-50 cursor-pointer transition-colors ${selectedEmpId === emp.employee_id ? "bg-blue-50" : "hover:bg-gray-50"}`}
                                                        >
                                                            <td className="px-4 py-3">
                                                                <div className="font-medium text-gray-900">{emp.employee_name ?? "—"}</div>
                                                                <div className="text-xs text-gray-400">{emp.employee_code}{emp.designation ? ` · ${emp.designation}` : ""}</div>
                                                            </td>
                                                            <td className="px-4 py-3">
                                                                <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${STATUS_STYLE[emp.status] ?? "bg-gray-100 text-gray-600"}`}>
                                                                    {emp.status}
                                                                </span>
                                                            </td>
                                                            <td className="px-4 py-3"><TimeCell signin={emp.signin} signout={emp.signout} /></td>
                                                            <td className="px-4 py-3">
                                                                <WorkDetailsCell locationType={emp.location_type} route={emp.route} taType={emp.ta_type} />
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        )}
                                    </div>
                                </div>

                                {/* Map */}
                                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" style={{ minHeight: 480 }}>
                                    <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
                                        <MapPin className="w-4 h-4 text-blue-500" />
                                        <h2 className="font-semibold text-gray-800">Check-in / Check-out Locations</h2>
                                        <div className="flex items-center gap-3 ml-auto text-xs text-gray-500">
                                            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-green-500 inline-block" />Check-in</span>
                                            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-red-500 inline-block" />Check-out</span>
                                        </div>
                                    </div>
                                    <div className="h-[440px]">
                                        {!isLoaded ? (
                                            <div className="h-full flex items-center justify-center text-gray-400 text-sm">Loading map…</div>
                                        ) : (
                                            <GoogleMap
                                                mapContainerStyle={MAP_CONTAINER}
                                                center={INDIA_CENTER}
                                                zoom={5}
                                                onLoad={m => { mapRef.current = m; }}
                                                options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: true }}
                                            >
                                                {(selectedEmpId ? filteredSubs.filter(e => e.employee_id === selectedEmpId) : filteredSubs)
                                                    .filter(e => e.start_latitude && e.start_longitude)
                                                    .map(emp => (
                                                        <React.Fragment key={emp.employee_id}>
                                                            <Marker
                                                                position={{ lat: emp.start_latitude!, lng: emp.start_longitude! }}
                                                                icon={checkinIcon}
                                                                title={`${emp.employee_name} — Check-in`}
                                                                onClick={() => setInfoEmp(emp)}
                                                            />
                                                            {emp.end_latitude && emp.end_longitude && (
                                                                <Marker
                                                                    position={{ lat: emp.end_latitude!, lng: emp.end_longitude! }}
                                                                    icon={checkoutIcon}
                                                                    title={`${emp.employee_name} — Check-out`}
                                                                    onClick={() => setInfoEmp(emp)}
                                                                />
                                                            )}
                                                        </React.Fragment>
                                                    ))}

                                                {infoEmp && infoEmp.start_latitude && infoEmp.start_longitude && (
                                                    <InfoWindow
                                                        position={{ lat: infoEmp.start_latitude, lng: infoEmp.start_longitude }}
                                                        onCloseClick={() => setInfoEmp(null)}
                                                    >
                                                        <div className="text-sm min-w-[160px]">
                                                            <p className="font-bold text-gray-900 mb-1">{infoEmp.employee_name}</p>
                                                            <p className="text-gray-500 text-xs mb-1">{infoEmp.designation}</p>
                                                            <div className="flex items-center gap-1 text-xs text-gray-600 mt-1">
                                                                <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
                                                                In: {fmtTime(infoEmp.signin)}
                                                            </div>
                                                            {infoEmp.signout && (
                                                                <div className="flex items-center gap-1 text-xs text-gray-600 mt-0.5">
                                                                    <span className="w-2 h-2 rounded-full bg-red-500 inline-block" />
                                                                    Out: {fmtTime(infoEmp.signout)}
                                                                </div>
                                                            )}
                                                            <div className="text-xs text-blue-600 mt-1 font-medium capitalize">
                                                                {infoEmp.location_type ? locLabel[infoEmp.location_type] ?? infoEmp.location_type : ""}
                                                            </div>
                                                            {infoEmp.route && (
                                                                <div className="text-xs text-gray-500 mt-0.5">{infoEmp.route}</div>
                                                            )}
                                                        </div>
                                                    </InfoWindow>
                                                )}
                                            </GoogleMap>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ════════════════════════════════════════ MONTHLY TAB */}
                    {tab === "monthly" && (
                        <div className="space-y-5">
                            {/* Controls */}
                            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex flex-wrap gap-3 items-center">
                                <div className="flex items-center gap-2">
                                    <User className="w-4 h-4 text-gray-400" />
                                    <select
                                        value={monthlyEmpId ?? ""}
                                        onChange={e => setMonthlyEmpId(Number(e.target.value))}
                                        className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[200px]"
                                    >
                                        <option value="" disabled>Select Employee</option>
                                        {employees.map(e => (
                                            <option key={e.id} value={e.id}>{e.name} ({e.code})</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Calendar className="w-4 h-4 text-gray-400" />
                                    <input
                                        type="month"
                                        value={monthlyMonth}
                                        max={toMonthStr(new Date())}
                                        onChange={e => setMonthlyMonth(e.target.value)}
                                        className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                {/* Month nav */}
                                <div className="flex items-center gap-1 ml-auto">
                                    <button
                                        onClick={() => {
                                            const [y, m] = monthlyMonth.split("-").map(Number);
                                            const d = new Date(y, m - 2, 1);
                                            setMonthlyMonth(toMonthStr(d));
                                        }}
                                        className="p-2 rounded-lg hover:bg-gray-100"
                                    ><ChevronLeft className="w-4 h-4" /></button>
                                    <button
                                        onClick={() => {
                                            const [y, m] = monthlyMonth.split("-").map(Number);
                                            const next = new Date(y, m, 1);
                                            if (next <= new Date()) setMonthlyMonth(toMonthStr(next));
                                        }}
                                        className="p-2 rounded-lg hover:bg-gray-100"
                                    ><ChevronRight className="w-4 h-4" /></button>
                                </div>
                            </div>

                            {monthlyLoading && <div className="bg-white rounded-xl border border-gray-200 p-8"><Skeleton rows={8} /></div>}
                            {monthlyError  && <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-red-500 text-sm">{monthlyError}</div>}

                            {monthlyData && !monthlyLoading && (
                                <>
                                    {/* Employee card + stats */}
                                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                                        <div className="flex items-start justify-between flex-wrap gap-4">
                                            <div>
                                                <h2 className="text-lg font-bold text-gray-900">{monthlyData.employee_name}</h2>
                                                <p className="text-sm text-gray-500">{monthlyData.designation} · {monthlyData.employee_code}</p>
                                                <p className="text-xs text-gray-400 mt-1">
                                                    {fmtMonthYear(monthlyData.month)}
                                                    {" · "}{monthlyData.working_days} working days
                                                </p>
                                            </div>
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                                <StatCard label="Present"  value={monthlyData.present_count}  color="bg-green-50 text-green-800" />
                                                <StatCard label="Absent"   value={monthlyData.absent_count}   color="bg-red-50 text-red-800" />
                                                <StatCard label="Leave"    value={monthlyData.leave_count}    color="bg-yellow-50 text-yellow-800" />
                                                <StatCard label="Holidays" value={monthlyData.holiday_count}  color="bg-purple-50 text-purple-800" />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Calendar */}
                                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                                        <h3 className="font-semibold text-gray-800 mb-4">Attendance Calendar</h3>
                                        {/* Legend */}
                                        <div className="flex flex-wrap gap-3 mb-4 text-xs">
                                            {[["present","bg-green-500","Present"],["absent","bg-red-400","Absent"],["leave","bg-yellow-400","Leave"],["holiday","bg-purple-400","Holiday"],["sunday","bg-gray-200","Sunday"]].map(([k, bg, label]) => (
                                                <span key={k} className="flex items-center gap-1.5"><span className={`w-3 h-3 rounded-sm ${bg} inline-block`} /><span className="text-gray-600">{label}</span></span>
                                            ))}
                                        </div>
                                        {/* Day headers */}
                                        <div className="grid grid-cols-7 mb-1">
                                            {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d => (
                                                <div key={d} className="text-center text-xs font-semibold text-gray-400 py-1">{d}</div>
                                            ))}
                                        </div>
                                        {/* Calendar cells */}
                                        <div className="grid grid-cols-7 gap-1">
                                            {calendarGrid.map((day, i) => (
                                                day === null ? (
                                                    <div key={`blank-${i}`} />
                                                ) : (
                                                    <button
                                                        key={day.date}
                                                        onClick={() => day.status === "present" && day.start_latitude ? setMapDay(day) : undefined}
                                                        className={`relative rounded-lg p-1.5 text-center transition-all ${STATUS_CELL[day.status] ?? "bg-gray-100 text-gray-500"}
                                                            ${day.status === "present" && day.start_latitude ? "cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-blue-400" : "cursor-default"}`}
                                                    >
                                                        <div className="text-xs font-bold leading-none">{new Date(day.date + "T00:00:00").getDate()}</div>
                                                        <div className="text-[9px] mt-0.5 opacity-80 leading-none">{day.day}</div>
                                                        {day.status === "present" && day.start_latitude && (
                                                            <MapPin className="w-2.5 h-2.5 absolute top-0.5 right-0.5 opacity-60" />
                                                        )}
                                                    </button>
                                                )
                                            ))}
                                        </div>
                                        <p className="text-xs text-gray-400 mt-3">Click a <span className="font-semibold text-green-700">present</span> day with a pin icon to see the location on map.</p>
                                    </div>

                                    {/* Map for selected day */}
                                    {mapDay && (
                                        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                                            <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    <MapPin className="w-4 h-4 text-blue-500" />
                                                    <h3 className="font-semibold text-gray-800">Location on {fmtDate(mapDay.date)}</h3>
                                                    <span className="text-xs text-gray-400">· {locLabel[mapDay.location_type ?? ""] ?? mapDay.location_type ?? ""}</span>
                                                    {mapDay.route && <span className="text-xs text-gray-400">· {mapDay.route}</span>}
                                                    <span className="text-xs font-mono text-gray-500 ml-2">
                                                        In: {fmtTime(mapDay.signin)} {mapDay.signout ? `· Out: ${fmtTime(mapDay.signout)}` : ""}
                                                    </span>
                                                </div>
                                                <button onClick={() => setMapDay(null)} className="p-1 rounded hover:bg-gray-100"><X className="w-4 h-4 text-gray-500" /></button>
                                            </div>
                                            <div style={{ height: 340 }}>
                                                {!isLoaded ? (
                                                    <div className="h-full flex items-center justify-center text-gray-400 text-sm">Loading map…</div>
                                                ) : (
                                                    <GoogleMap
                                                        mapContainerStyle={MAP_CONTAINER}
                                                        center={{ lat: mapDay.start_latitude!, lng: mapDay.start_longitude! }}
                                                        zoom={14}
                                                        options={{ streetViewControl: false, mapTypeControl: false }}
                                                    >
                                                        <Marker
                                                            position={{ lat: mapDay.start_latitude!, lng: mapDay.start_longitude! }}
                                                            icon={checkinIcon}
                                                            title="Check-in"
                                                        />
                                                        {mapDay.end_latitude && mapDay.end_longitude && (
                                                            <Marker
                                                                position={{ lat: mapDay.end_latitude!, lng: mapDay.end_longitude! }}
                                                                icon={checkoutIcon}
                                                                title="Check-out"
                                                            />
                                                        )}
                                                    </GoogleMap>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Daily records table */}
                                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                                        <div className="px-5 py-3 border-b border-gray-100">
                                            <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                                                <Clock className="w-4 h-4 text-blue-500" /> Daily Records
                                            </h3>
                                        </div>
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-sm">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        {["Date","Day","Status","Time","Work Details","GPS"].map(h => (
                                                            <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {monthlyData.daily.filter(d => d.status === "present" || d.status === "absent" || d.status === "leave").map(d => (
                                                        <tr key={d.date} className="border-t border-gray-50 hover:bg-gray-50">
                                                            <td className="px-4 py-3 font-medium text-gray-900 text-xs">{fmtDate(d.date)}</td>
                                                            <td className="px-4 py-3 text-gray-500 text-xs">{d.day}</td>
                                                            <td className="px-4 py-3">
                                                                <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${STATUS_STYLE[d.status] ?? "bg-gray-100"}`}>
                                                                    {d.status === "leave" && d.leave_type ? d.leave_type : d.status}
                                                                </span>
                                                            </td>
                                                            <td className="px-4 py-3"><TimeCell signin={d.signin} signout={d.signout} /></td>
                                                            <td className="px-4 py-3">
                                                                <WorkDetailsCell locationType={d.location_type} route={d.route} taType={d.ta_type} />
                                                            </td>
                                                            <td className="px-4 py-3">
                                                                {d.start_latitude ? (
                                                                    <button
                                                                        onClick={() => setMapDay(d)}
                                                                        className="text-blue-600 hover:text-blue-800 transition-colors"
                                                                        title="View on map"
                                                                    >
                                                                        <MapPin className="w-4 h-4" />
                                                                    </button>
                                                                ) : <span className="text-gray-300">—</span>}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>
        </PageLayout>
    );
};

export default AttendancePage;
